import { useMemo } from 'react';
import { useAsyncValue } from 'framework/utils';
import { EMPLOYEE_TYPES_ALIAS } from './constants';
import { EmployeeDisplayDataByAlias } from './types';
import useEmployeeNamesService from './employeeNames/employeeNamesService';
import useEmployeeService from './employeeService';
import { indexEmployeeDisplayDataByAlias } from './utils';

const useEmployeeDisplayDataByAliases = (aliases: string[]) => {
  const { getEmployeeNamesByAliases } = useEmployeeNamesService();
  const { searchWithContractors } = useEmployeeService();
  const normalizedAliases = useMemo(
    () => Array.from(new Set(aliases.map(alias => alias.trim()).filter(Boolean))),
    [aliases],
  );

  return (
    useAsyncValue(async (): Promise<EmployeeDisplayDataByAlias> => {
      if (!normalizedAliases.length) {
        return {};
      }

      try {
        const [employeeNamesResponse, contractorsResponse] = await Promise.all([
          getEmployeeNamesByAliases(normalizedAliases),
          searchWithContractors({
            filter: {
              alias: { in: normalizedAliases },
              employeeType: { eq: EMPLOYEE_TYPES_ALIAS.CONTRACTOR },
            },
            pageSize: normalizedAliases.length,
            pageNumber: 0,
          }),
        ]);

        return indexEmployeeDisplayDataByAlias(employeeNamesResponse, contractorsResponse.result || []);
      } catch {
        return {};
      }
    }, normalizedAliases) || {}
  );
};

export default useEmployeeDisplayDataByAliases;
